import type { AppNotification } from '@alison/ui';
import DiamondOutlined from '@mui/icons-material/DiamondOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import NotificationsNoneOutlined from '@mui/icons-material/NotificationsNoneOutlined';
import RefreshOutlined from '@mui/icons-material/RefreshOutlined';
import StarBorder from '@mui/icons-material/StarBorder';
import TrendingUpOutlined from '@mui/icons-material/TrendingUpOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import type { inferOutput } from '@trpc/tanstack-react-query';
import { ROLES } from '../data/headerMocks.js';
import type { useTRPC } from './trpc.js';

const DEFAULT_ICON: AppNotification['Icon'] = NotificationsNoneOutlined;

const CATEGORY_ICONS: Record<AppNotification['category'], AppNotification['Icon']> = {
  performance: TrendingUpOutlined,
  anomaly: WarningAmberOutlined,
  opportunity: DiamondOutlined,
  portfolio: StarBorder,
  workflow: RefreshOutlined,
  system: InfoOutlined,
};

const MINUTE_MS = 60_000;

type Trpc = ReturnType<typeof useTRPC>;
export type FeedRow = inferOutput<Trpc['notifications']['feed']['list']>[number];

export function formatRelativeTime(date: Date, now: Date = new Date()): string {
  const minutes = Math.floor((now.getTime() - date.getTime()) / MINUTE_MS);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  if (hours < 48) return 'Yesterday';
  return date.toLocaleDateString();
}

export function unreadFor(notifications: AppNotification[], roleId: string): number {
  return notifications.filter(
    (notification) => !notification.read && notification.roles.includes(roleId),
  ).length;
}

export function mapNotification(row: FeedRow): AppNotification {
  return {
    id: String(row.id),
    category: row.category,
    tone: row.tone,
    Icon: CATEGORY_ICONS[row.category] ?? DEFAULT_ICON,
    time: formatRelativeTime(new Date(row.createdAt)),
    roles: row.role === 'all' ? ROLES.map((role) => role.id) : [row.role],
    title: row.title,
    body: row.body,
    read: row.read,
    ...(row.scope != null && { scope: row.scope }),
    ...(row.actionLabel != null && { actionLabel: row.actionLabel }),
  };
}
